// src/utils/shootOff.ts
//
// Rozstřely ve výsledcích (přání majitele 2. 10. 2026): při shodě bodů
// rozhoduje o pořadí pole `tiebreaker` záznamu results_entries. Má stejný
// význam jako live_results.tiebreaker živého bodování (AdminLiveResults,
// DisplayView): vyšší = lepší, 0 / chybí = bez rozstřelu; skupina n střelců
// dostane od prvního (n × 0,001) po posledního (1 × 0,001).
//
// Zapisuje ho publikace z živého bodování (publishResults – přenos
// z live_results) a obrazovka Rozstřely v administraci (AdminShootOffs)
// u importovaných výsledků; nový import ho zachová, když skupina shodných
// bodů zůstala ve stejném složení (carryOverTiebreakers). Shoda bodů =
// přesně stejné matchPoints (body z PractiScore mívají víc desetinných míst,
// než se zobrazí); shoda 0 bodů (bez výsledku, DQ) se jako shoda nebere –
// stejně jako v živém bodování (AdminLiveResults: totalScore > 0).

//
// Fáze 4a živého bodování (docs/live-faze4-navrh.md, bod C2 a otázka 4):
// rozstřel platí jen pro skupinu, pro kterou byl zadán. Hodnota nese v
// posledních číslicích OTISK SKUPINY: (n − i) × 0,001 + otisk × 0,0000001,
// otisk = 1…997 z identit členů skupiny (shootOffTag). Hodnota zůstává
// v (0; 0,1] pro skupiny do 99 střelců (pravidla judgeTiebreakerWrite beze
// změny) a krok 0,001 dál převažuje – starší verze aplikace řadí stejně.
// Skupina (stejné body > 0 v zobrazeném seznamu) rozstřelem rozhodnutá je,
// jen když má každý člen hodnotu, všechny otisky sedí na tuto skupinu
// a kroky jsou přesně 1…n; jinak se hodnoty nepoužijí (resolveShootOff).
// Hodnoty BEZ otisku (zadané před fází 4a, import) platí jako dřív.
//
// Rozstřel je samostatné pole – nikdy se nepřičítá k bodům. Body terčů
// nesou srdeční zásahy v desetinách (30,1), otisk je v desetimilióntinách
// jiného pole; nemíchají se (test v shootOff.test.ts).

/** Krok hodnoty rozstřelu – jako živé bodování. */
export const SHOOT_OFF_STEP = 0.001;

/** Jednotka otisku: hodnota × 10⁷ = krok × 10 000 + otisk. */
const SHOOT_OFF_UNITS = 1e7;
const SHOOT_OFF_STEP_UNITS = 10000;
/** Otisk skupiny je 1…997. */
export const SHOOT_OFF_TAG_MOD = 997;
/** Největší skupina s otiskem (n × 0,001 + otisk ≤ 0,1 – pravidla). */
export const SHOOT_OFF_MAX_TAGGED = 99;

/** Rozstřel záznamu: kladné konečné číslo, jinak 0 (chybí, poškozená hodnota). */
export function tiebreakerOf(entry: { tiebreaker?: unknown } | null | undefined): number {
  const value = entry ? entry.tiebreaker : undefined;
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Hodnoty rozstřelu BEZ otisku pro skupinu v daném pořadí: první dostane
 * count × krok, poslední 1 × krok. Zaokrouhleno na tisíciny, ať se do
 * dokumentu nezapisuje 0.30000000000000004. Od fáze 4a jen pro skupiny nad
 * SHOOT_OFF_MAX_TAGGED (shootOffValues).
 */
export function tiebreakerValues(count: number): number[] {
  const values: number[] = [];
  for (let i = 0; i < count; i++) {
    values.push(Math.round((count - i) * SHOOT_OFF_STEP * 1000) / 1000);
  }
  return values;
}

/** Identita člena skupiny pro otisk (startovní číslo živého bodování, jinak klíč záznamu). */
export type ShootOffId = string | number;

/**
 * Otisk skupiny 1…997 z identit jejích členů (na pořadí nezáleží). Živé
 * bodování (tabule, Živé výsledky) i zveřejněné výsledky živého závodu
 * počítají ze startovních čísel, import z klíčů záznamů (shootOffIdOf).
 */
export function shootOffTag(ids: ReadonlyArray<ShootOffId>): number {
  const key = ids.map((id) => String(id)).sort().join("|");
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) % 1000003;
  return (hash % SHOOT_OFF_TAG_MOD) + 1;
}

/**
 * Hodnoty rozstřelu s otiskem pro skupinu v daném pořadí (první = vítěz):
 * (n − i) × 0,001 + otisk × 10⁻⁷. Skupina nad 99 střelců (hodnota by
 * přesáhla 0,1) dostane hodnoty bez otisku jako dřív.
 */
export function shootOffValues(orderedIds: ReadonlyArray<ShootOffId>): number[] {
  const n = orderedIds.length;
  if (n > SHOOT_OFF_MAX_TAGGED) return tiebreakerValues(n);
  const tag = shootOffTag(orderedIds);
  return orderedIds.map((_, i) => ((n - i) * SHOOT_OFF_STEP_UNITS + tag) / SHOOT_OFF_UNITS);
}

/** Hodnota rozstřelu rozložená na krok (1 = poslední) a otisk (0 = bez otisku). */
export function decodeShootOff(value: number): { step: number; tag: number } | null {
  if (!(typeof value === "number" && Number.isFinite(value) && value > 0)) return null;
  const units = Math.round(value * SHOOT_OFF_UNITS);
  const tag = units % SHOOT_OFF_STEP_UNITS;
  return {
    step: Math.floor(units / SHOOT_OFF_STEP_UNITS),
    tag: tag >= 1 && tag <= SHOOT_OFF_TAG_MOD ? tag : 0,
  };
}

/** Stav rozstřelu skupiny. */
export type ShootOffState = "decided" | "partial" | "open";

export interface ShootOffResolution {
  /**
   * "decided" = pořadí skupiny určuje rozstřel, "partial" = rozstřel jen
   * u části / neplatný pro tuto skupinu, "open" = bez rozstřelu.
   */
  state: ShootOffState;
  /** Některý člen má hodnotu s otiskem (zadanou od fáze 4a). */
  tagged: boolean;
  /** Hodnoty, podle kterých se ve skupině řadí (stejné pořadí jako vstup); 0 = nepoužije se. */
  effective: number[];
}

/**
 * Rozstřel skupiny shodných bodů (ids a values ve stejném pořadí):
 *  - bez hodnot s otiskem (starší rozstřely, import) – jako dřív: hodnoty
 *    platí samy o sobě, "decided" = každý člen má jinou kladnou hodnotu,
 *  - s otiskem – platí, jen když má hodnotu každý člen, všechny otisky jsou
 *    otiskem TÉTO skupiny a kroky jsou přesně 1…n; jinak se nepoužije žádná
 *    (zastaralý rozstřel ze dřívější shody, rozstřel jiné kategorie).
 */
export function resolveShootOff(
  ids: ReadonlyArray<ShootOffId>,
  values: ReadonlyArray<number>
): ShootOffResolution {
  const positive = values.map((v) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0));
  const withValue = positive.filter((v) => v > 0);
  const decoded = positive.map((v) => (v > 0 ? decodeShootOff(v) : null));
  const tagged = decoded.some((d) => !!d && d.tag > 0);
  if (!tagged) {
    const distinct = new Set(withValue).size;
    const state: ShootOffState =
      withValue.length === 0
        ? "open"
        : withValue.length === positive.length && distinct === positive.length
        ? "decided"
        : "partial";
    return { state, tagged: false, effective: positive };
  }
  const tag = shootOffTag(ids);
  const n = positive.length;
  const steps = new Set<number>();
  let valid = true;
  decoded.forEach((d) => {
    if (!d || d.tag !== tag || d.step < 1 || d.step > n || steps.has(d.step)) valid = false;
    else steps.add(d.step);
  });
  if (valid && steps.size === n) return { state: "decided", tagged: true, effective: positive };
  return { state: "partial", tagged: true, effective: positive.map(() => 0) };
}

/**
 * Identita záznamu results_entries pro otisk: záznam živého bodování se
 * startovním číslem (jako tabule), jinak klíč záznamu (import, starší
 * publikace).
 */
export function shootOffIdOf(entry: { source?: unknown; startNumber?: unknown; sequenceNumber?: unknown }): string {
  if (entry.source === "live_scoring" && typeof entry.startNumber === "number" && Number.isFinite(entry.startNumber)) {
    return String(entry.startNumber);
  }
  return String(entry.sequenceNumber);
}

/**
 * Seřadí běhy shodných bodů podle platného rozstřelu (resolveShootOff) –
 * vstup musí být seřazený podle bodů sestupně; pořadí uvnitř shody bez
 * platného rozstřelu zůstane podle vstupu (stabilní řazení). Body ≤ 0 se
 * řadí podle hodnot jako dřív (shoda 0 bodů se za shodu nebere).
 */
export function orderByShootOff<T>(
  sortedByPoints: ReadonlyArray<T>,
  pointsOf: (item: T) => number,
  idOf: (item: T) => ShootOffId,
  tiebreakOf: (item: T) => number
): T[] {
  const out: T[] = [];
  let start = 0;
  while (start < sortedByPoints.length) {
    const points = pointsOf(sortedByPoints[start]);
    let end = start + 1;
    while (end < sortedByPoints.length && pointsOf(sortedByPoints[end]) === points) end++;
    const run = sortedByPoints.slice(start, end);
    if (run.length < 2) {
      out.push(...run);
    } else {
      const values = run.map(tiebreakOf);
      const effective = points > 0 ? resolveShootOff(run.map(idOf), values).effective : values;
      const indexed = run.map((item, i) => ({ item, i, v: effective[i] }));
      indexed.sort((a, b) => b.v - a.v || a.i - b.i);
      indexed.forEach((x) => out.push(x.item));
    }
    start = end;
  }
  return out;
}

export interface TieGroup<T> {
  /** Společné body skupiny. */
  matchPoints: number;
  /** Místa, která skupina v celkovém pořadí zabírá (1 = první). */
  fromRank: number;
  toRank: number;
  /** Členové skupiny v aktuálním pořadí (tak, jak jdou v seřazených výsledcích). */
  members: T[];
  /**
   * "decided" = každý člen má jiný kladný rozstřel (pořadí je rozhodnuté),
   * "partial" = rozstřel má jen část členů nebo jsou hodnoty shodné
   * (typicky po doplnění dalšího střelce se stejnými body), "open" = nikdo.
   * Od fáze 4a s otiskem skupiny (resolveShootOff): rozstřel zadaný pro
   * jinou skupinu je "partial".
   */
  state: ShootOffState;
}

/**
 * Skupiny shodných bodů (aspoň 2 střelci, body > 0) v SEŘAZENÝCH výsledcích
 * (sortRaceEntries – body sestupně, při shodě rozstřel sestupně). `idOf`
 * (identita pro otisk skupiny – shootOffIdOf) – bez ní se stav počítá jako
 * dřív jen z hodnot.
 */
export function findTieGroups<T>(
  sorted: ReadonlyArray<T>,
  pointsOf: (item: T) => number,
  tiebreakOf: (item: T) => number,
  idOf?: (item: T) => ShootOffId
): TieGroup<T>[] {
  const groups: TieGroup<T>[] = [];
  let start = 0;
  while (start < sorted.length) {
    const points = pointsOf(sorted[start]);
    let end = start + 1;
    while (end < sorted.length && pointsOf(sorted[end]) === points) end++;
    if (end - start >= 2 && points > 0) {
      const members = sorted.slice(start, end);
      const values = members.map(tiebreakOf);
      let state: ShootOffState;
      if (idOf) {
        state = resolveShootOff(members.map(idOf), values).state;
      } else {
        const withValue = values.filter((v) => v > 0);
        const distinct = new Set(withValue).size;
        state =
          withValue.length === 0
            ? "open"
            : withValue.length === members.length && distinct === members.length
            ? "decided"
            : "partial";
      }
      groups.push({ matchPoints: points, fromRank: start + 1, toRank: end, members, state });
    }
    start = end;
  }
  return groups;
}

/**
 * Klíče záznamů, jejichž pořadí určil rozstřel (štítek „R" ve výsledcích):
 * střelci s rozstřelem ve skupině shodných bodů (> 0), jejichž hodnota je ve
 * skupině jedinečná, a takoví aspoň dva – jako živá tabule (DisplayView).
 * Střelec bez rozstřelu (např. doplněný do skupiny později) R nemá, stejně
 * jako rozstřel, po kterém shoda zmizela, nebo shodné hodnoty ze dvou
 * různých rozstřelů (např. po kategoriích na tabuli – o pořadí mezi nimi
 * rozhodlo jen číslo).
 *
 * Fáze 4a: s `idOf` se skupina s hodnotami s otiskem (resolveShootOff)
 * označí celá, jen když je rozhodnutá pro tuto skupinu, jinak vůbec.
 * `only` omezí výsledek na skupiny bez otisku ("legacy") nebo s ním
 * ("tagged") – výsledky při filtru kategorie berou starší rozstřely z celého
 * závodu (beze změny) a nové z kategorie.
 */
export function shootOffKeys<T>(
  items: ReadonlyArray<T>,
  keyOf: (item: T) => string,
  pointsOf: (item: T) => number,
  tiebreakOf: (item: T) => number,
  idOf?: (item: T) => ShootOffId,
  only?: "legacy" | "tagged"
): Set<string> {
  const byPoints = new Map<number, T[]>();
  items.forEach((item) => {
    const points = pointsOf(item);
    const list = byPoints.get(points);
    if (list) list.push(item);
    else byPoints.set(points, [item]);
  });
  const keys = new Set<string>();
  byPoints.forEach((list, points) => {
    if (points <= 0) return;
    if (idOf && list.length >= 2) {
      const resolution = resolveShootOff(list.map(idOf), list.map(tiebreakOf));
      if (resolution.tagged) {
        if (only === "legacy") return;
        if (resolution.state === "decided") list.forEach((item) => keys.add(keyOf(item)));
        return;
      }
    }
    if (only === "tagged") return;
    const counts = new Map<number, number>();
    list.forEach((item) => {
      const value = tiebreakOf(item);
      if (value > 0) counts.set(value, (counts.get(value) || 0) + 1);
    });
    const decided = list.filter((item) => counts.get(tiebreakOf(item)) === 1);
    if (decided.length < 2) return;
    decided.forEach((item) => keys.add(keyOf(item)));
  });
  return keys;
}

/** Uložený záznam pro přenos rozstřelu při novém importu. */
export interface StoredShootOff {
  matchPoints: unknown;
  tiebreaker?: unknown;
  /** Zdroj a startovní číslo uloženého záznamu – identita pro otisk (shootOffIdOf). */
  source?: unknown;
  startNumber?: unknown;
}

/** Klíče podle bodů (jen kladné body). */
function keysByPoints(entries: ReadonlyArray<{ key: string; points: number }>): Map<number, string[]> {
  const map = new Map<number, string[]>();
  entries.forEach((e) => {
    if (!(e.points > 0)) return;
    const list = map.get(e.points);
    if (list) list.push(e.key);
    else map.set(e.points, [e.key]);
  });
  return map;
}

/** Plán rozstřelů při importu – viz planImportTiebreakers. */
export interface ImportTiebreakers {
  /** Přepisované záznamy, kterým se rozstřel zachová (klíč → hodnota). */
  carry: Map<string, number>;
  /** Ponechané záznamy (import bez „Nahradit"), kterým se rozstřel smaže. */
  clear: Set<string>;
}

/**
 * Nový import přepisuje dokumenty celé – rozstřel vyznačený u dřívějších
 * výsledků platí dál, jen když skupina shodných bodů zůstala STEJNÁ: tytéž
 * klíče se stejnými body před importem i po něm (`finalEntries` = celý stav
 * závodu po importu, i záznamy, které soubor nepřepisuje). Jiné body nebo
 * jiné složení skupiny = jiná situace a rozstřel neplatí CELÉ skupině:
 * přepisovaným se nepřenese a ponechaným (import bez „Nahradit") se smaže –
 * jinak by zbytek staré hodnoty určoval pořadí v nové shodě.
 */
export function planImportTiebreakers(
  finalEntries: ReadonlyArray<{ sequenceNumber: string; matchPoints: number }>,
  stored: ReadonlyMap<string, StoredShootOff>,
  rewritten: ReadonlySet<string>
): ImportTiebreakers {
  const before = keysByPoints(
    Array.from(stored.entries()).map(([key, old]) => ({
      key,
      points: typeof old.matchPoints === "number" ? old.matchPoints : NaN,
    }))
  );
  const after = keysByPoints(
    finalEntries.map((e) => ({ key: e.sequenceNumber, points: e.matchPoints }))
  );
  const sameGroup = (points: number): boolean => {
    const a = before.get(points) || [];
    const b = after.get(points) || [];
    if (a.length < 2 || a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((key) => set.has(key));
  };

  // Fáze 4a (C2, nález L3 kontroly): rozstřel s otiskem platí jen pro
  // skupinu identit, pro kterou byl zadán. Přepsaný záznam má po importu
  // identitu = klíč (import), ponechaný si nese svou (živý záznam = startovní
  // číslo). Skupina s hodnotou s otiskem, která by po importu nebyla
  // rozhodnutá (resolveShootOff), se nepřenese a ponechaným se smaže –
  // bezpečnější než ponechat hodnotu, kterou by výsledky tiše ignorovaly
  // (Rozstřely v administraci ukážou „Nerozhodnuto“ a rozstřel se zadá znovu).
  // Skupina bez přepsaného záznamu se nemění. Hodnoty bez otisku jako dřív.
  const groupValid = new Map<number, boolean>();
  const taggedGroupValid = (points: number): boolean => {
    const cached = groupValid.get(points);
    if (cached !== undefined) return cached;
    const keys = after.get(points) || [];
    let valid = true;
    if (keys.some((key) => rewritten.has(key))) {
      const values = keys.map((key) => tiebreakerOf(stored.get(key)));
      const ids = keys.map((key) =>
        rewritten.has(key) ? key : shootOffIdOf({ ...(stored.get(key) || {}), sequenceNumber: key })
      );
      const resolution = resolveShootOff(ids, values);
      valid = !resolution.tagged || resolution.state === "decided";
    }
    groupValid.set(points, valid);
    return valid;
  };

  const carry = new Map<string, number>();
  const clear = new Set<string>();
  finalEntries.forEach((entry) => {
    const old = stored.get(entry.sequenceNumber);
    if (!old) return;
    const value = tiebreakerOf(old);
    if (value <= 0) return;
    const keep =
      old.matchPoints === entry.matchPoints && sameGroup(entry.matchPoints) && taggedGroupValid(entry.matchPoints);
    if (rewritten.has(entry.sequenceNumber)) {
      if (keep) carry.set(entry.sequenceNumber, value);
    } else if (!keep) {
      clear.add(entry.sequenceNumber);
    }
  });
  return { carry, clear };
}

/** Jen přenášené hodnoty (planImportTiebreakers.carry). */
export function carryOverTiebreakers(
  finalEntries: ReadonlyArray<{ sequenceNumber: string; matchPoints: number }>,
  stored: ReadonlyMap<string, StoredShootOff>,
  rewritten: ReadonlySet<string>
): Map<string, number> {
  return planImportTiebreakers(finalEntries, stored, rewritten).carry;
}
